import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HistoryState, PaginationMetadata } from "../../types";
import { axiosObj } from "../../../config/apiClients";
import { formatTimeDiff, toUAETime } from "../../../utils/date";
import { queryParams } from "../../../utils/interface";

const initialPagination: PaginationMetadata = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false,
};

const readPagination = (payload: any): PaginationMetadata => ({
  totalItems: payload?.totalItems ?? 0,
  currentPage: payload?.page ?? 1,
  totalPages: payload?.totalPages ?? 1,
  hasNextPage: payload?.hasNextPage ?? false,
});

const initialState: HistoryState = {
  listLoading: { history: false },
  listPagination: { history: { ...initialPagination } },
  history: [],
};

export const getHistory = createAsyncThunk(
  "/statement/getAll",
  async ({ paramPage, paramLimit, search }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`statement/getAll?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch History' });
    }
  }
);

const mapToHistory = (data: any[]) => {
  return data.map((item) => {
    const openingTime = new Date(item.openingTime);
    const closingTime = new Date(item.closingTime);
    const timeDiffMs = closingTime.getTime() - openingTime.getTime();

    return {
      _id: item._id,
      account: item.account,
      openingTime: toUAETime(item.openingTime),
      script: item.script,
      id: item.orderId,
      tradeType: item.tradeType,
      tradeMoney: item.tradeMoney ? item.tradeMoney.toFixed(2) : '-',
      tradeMetal: item.tradeMetal ? item.tradeMetal.toFixed(2) : '-',
      openPrice: item.openPrice ? item.openPrice.toFixed(2) : '-',
      closingTime: toUAETime(item.closingTime),
      takeProfit: item.takeProfit ? item.takeProfit : '-',
      timeDiff: formatTimeDiff(timeDiffMs),
      commission: item.commission,
      closePrice: item.closePrice ? item.closePrice.toFixed(2) : '-',
      pnl: item.pnl ? item.pnl.toFixed(2) : '-',
      comment: item.comment,
      tradeStatus: item.tradeStatus,
      dealId: item.dealId,
      orderId: item.orderId,
      userId: item.userId,
      tradeSymbol: item.tradeSymbol
    };
  });
};

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getHistory.pending, (state) => {
        state.listLoading.history = true;
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        state.listLoading.history = false;
        state.history = mapToHistory(action.payload.payload.allTrades);
        state.listPagination.history = readPagination(action.payload.payload);
      })
      .addCase(getHistory.rejected, (state) => {
        state.listLoading.history = false;
        state.history = [];
      });
  }
})

export default historySlice.reducer;
